import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { Star, RotateCcw, Volume2 } from "lucide-react";
import { type Difficulty, getDifficultyConfig } from "./difficulty";
import { useSpeech } from "@/hooks/useSpeech";

const SENTENCES = [
  { words: ["أنا", "أحب", "أمي"], hint: "عبارة عن الحب" },
  { words: ["الشمس", "تشرق", "صباحاً"], hint: "عن الصباح" },
  { words: ["القطة", "تشرب", "الحليب"], hint: "حيوان يشرب" },
  { words: ["الطفل", "يلعب", "بالكرة"], hint: "لعب" },
  { words: ["أحب", "قراءة", "القصص"], hint: "هواية القراءة" },
  { words: ["الزهرة", "جميلة", "الرائحة"], hint: "عن الزهور" },
  { words: ["أغسل", "يديّ", "بالماء"], hint: "نظافة" },
  { words: ["السمكة", "تسبح", "في", "البحر"], hint: "حيوان بحري" },
  { words: ["الأرنب", "يأكل", "الجزر"], hint: "حيوان يأكل" },
  { words: ["نجوم", "السماء", "لامعة"], hint: "عن الليل" },
  { words: ["أذهب", "إلى", "المدرسة"], hint: "مكان التعلم" },
  { words: ["الفراشة", "تطير", "في", "الحديقة"], hint: "حشرة جميلة" },
  { words: ["أكتب", "بالقلم", "واجبي"], hint: "دراسة" },
  { words: ["الطائر", "يغرّد", "فوق", "الشجرة"], hint: "صوت جميل" },
  { words: ["الأم", "تطبخ", "الطعام"], hint: "في المطبخ" },
];

const shuffleArray = <T,>(arr: T[]): T[] => [...arr].sort(() => Math.random() - 0.5);

interface Props {
  onComplete?: (result: { score: number; maxScore: number; stars: number }) => void;
  difficulty?: Difficulty;
}

export default function WordOrderGame({ onComplete, difficulty = "medium" }: Props) {
  const { rounds: totalRounds } = getDifficultyConfig(difficulty);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [currentSentence, setCurrentSentence] = useState(SENTENCES[0]);
  const [shuffledWords, setShuffledWords] = useState<string[]>([]);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  const [availableWords, setAvailableWords] = useState<string[]>([]);
  const [showResult, setShowResult] = useState<boolean | null>(null);
  const [completed, setCompleted] = useState(false);

  const { speak, encourageCorrect, encourageWrong } = useSpeech();

  const setupRound = () => {
    const sentence = SENTENCES[Math.floor(Math.random() * SENTENCES.length)];
    setCurrentSentence(sentence);
    const shuffled = shuffleArray([...sentence.words]);
    setShuffledWords(shuffled);
    setAvailableWords(shuffled);
    setSelectedWords([]);
    setShowResult(null);
  };

  useEffect(() => {
    setupRound();
  }, []);

  const addWord = (word: string, index: number) => {
    if (showResult !== null) return;
    const newSelected = [...selectedWords, word];
    setSelectedWords(newSelected);
    const newAvailable = [...availableWords];
    newAvailable.splice(index, 1);
    setAvailableWords(newAvailable);

    // Check if complete
    if (newSelected.length === currentSentence.words.length) {
      const correct = newSelected.every((w, i) => w === currentSentence.words[i]);
      setShowResult(correct);
      if (correct) {
        setScore((s) => s + 10);
        encourageCorrect();
      } else {
        encourageWrong();
      }
      setTimeout(() => {
        setRound((r) => r + 1);
        setupRound();
      }, 1500);
    }
  };

  const removeWord = (index: number) => {
    if (showResult !== null) return;
    const word = selectedWords[index];
    const newSelected = [...selectedWords];
    newSelected.splice(index, 1);
    setSelectedWords(newSelected);
    setAvailableWords((prev) => [...prev, word]);
  };

  const reset = () => {
    setScore(0);
    setRound(0);
    setCompleted(false);
    setupRound();
  };

  useEffect(() => {
    if (round >= totalRounds && !completed) {
      setCompleted(true);
      const maxScore = totalRounds * 10;
      const starCount = Math.ceil((score / maxScore) * 5);
      onComplete?.({ score, maxScore, stars: starCount });
    }
  }, [round]);

  if (round >= totalRounds) {
    return (
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <Card className="max-w-md mx-auto text-center shadow-elevated">
          <CardContent className="p-8 space-y-6">
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 1.5 }} className="text-6xl">📝</motion.div>
            <h2 className="text-2xl font-bold text-foreground">أحسنت! 🎉</h2>
            <p className="text-muted-foreground">حصلت على <span className="text-primary font-bold text-xl">{score}</span> نقطة</p>
            <div className="flex justify-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-8 h-8 ${i < Math.ceil((score / (totalRounds * 10)) * 5) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
              ))}
            </div>
            <Button onClick={reset} size="lg" className="gradient-hero border-0 text-primary-foreground">
              <RotateCcw className="w-4 h-4 ml-2" />
              اللعب مرة أخرى
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <Badge variant="secondary" className="text-sm px-3 py-1">⭐ {score} نقطة</Badge>
        <Badge variant="outline" className="text-sm px-3 py-1">{round + 1} / {totalRounds}</Badge>
      </div>

      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500" animate={{ width: `${((round + 1) / totalRounds) * 100}%` }} />
      </div>

      <Card className="shadow-elevated overflow-hidden">
        <CardContent className="p-0">
          <div className="bg-gradient-to-br from-cyan-400 to-blue-500 p-6 text-center text-white relative">
            <Button variant="ghost" size="icon" className="absolute top-3 left-3 h-8 w-8 text-white/80 hover:text-white hover:bg-white/20 rounded-full" onClick={() => speak("رتّب الكلمات: " + currentSentence.words.join(" "))}>
              <Volume2 className="w-4 h-4" />
            </Button>
            <motion.p className="text-4xl mb-2" animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 2.5 }}>📝</motion.p>
            <p className="text-lg font-bold">رتّب الكلمات لتكوّن جملة صحيحة</p>
            <p className="text-sm opacity-80 mt-1">💡 تلميح: {currentSentence.hint}</p>
          </div>
        </CardContent>
      </Card>

      {/* Selected words area */}
      <div className={`min-h-[60px] rounded-xl border-2 border-dashed p-3 flex flex-wrap gap-2 items-center justify-center transition-colors ${
        showResult === true ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" :
        showResult === false ? "border-red-400 bg-red-50 dark:bg-red-900/20" :
        "border-muted-foreground/30 bg-muted/30"
      }`}>
        {selectedWords.length === 0 && (
          <p className="text-sm text-muted-foreground">اضغط على الكلمات بالترتيب الصحيح</p>
        )}
        <AnimatePresence>
          {selectedWords.map((word, i) => (
            <motion.button
              key={`selected-${i}-${word}`}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              whileHover={showResult === null ? { scale: 1.1 } : {}}
              onClick={() => removeWord(i)}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-bold text-lg shadow-sm"
            >
              {word}
            </motion.button>
          ))}
        </AnimatePresence>
        {showResult === true && (
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-2xl">✅</motion.span>
        )}
        {showResult === false && (
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-2xl">❌</motion.span>
        )}
      </div>

      {/* Available words */}
      <div className="flex flex-wrap gap-3 justify-center">
        <AnimatePresence>
          {availableWords.map((word, i) => (
            <motion.button
              key={`avail-${i}-${word}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: i * 0.05 }}
              whileHover={{ scale: 1.1, rotate: [-2, 2, 0] }}
              whileTap={{ scale: 0.9 }}
              onClick={() => addWord(word, i)}
              className="bg-card border-2 border-border hover:border-primary/50 px-5 py-3 rounded-xl font-bold text-lg text-foreground shadow-card transition-colors"
            >
              {word}
            </motion.button>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
